import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const AddNewProduct = ({ setActiveView, selectedCategory }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageURL: '',
    uom: '',
    category: selectedCategory || '',
    metadata: {}
  });
  const [metadataInput, setMetadataInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uomsLoading, setUomsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState('');
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: ''
  });
  const [showNewCategoryFields, setShowNewCategoryFields] = useState(false);
  const { isAuthenticated, verifySession, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await axios.get('/categories/getAllCategories', {
          withCredentials: true
        });
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError(error.response?.data?.message || 'Failed to load categories');
      } finally {
        setLoading(false);
      }
    };

    const fetchUoms = async () => {
      try {
        setUomsLoading(true);
        // First verify the session to ensure we have valid credentials
        await verifySession();
        
        // Make the UOM request with credentials
        const response = await axios.get('/uoms', {
          withCredentials: true
        });
        
        setUoms(response.data.data || response.data); // Handle both response structures
      } catch (error) {
        console.error('Error fetching UOMs:', error);
        // Don't show error to user as UOM is not a required field
      } finally {
        setUomsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchCategories();
      fetchUoms();
    }
  }, [isAuthenticated, verifySession]);

  const parseMetadata = (input) => {
    const metadata = {};
    const lines = input.split('\n');
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        const [key, value] = trimmedLine.split(':').map(part => part.trim().replace(/^"|"$/g, ''));
        if (key && value) {
          metadata[key] = value;
        }
      }
    });
    
    return metadata;
  };

  const handleImagePreview = () => {
    setImageError('');
    if (!formData.imageURL) {
      setImagePreview(null);
      return;
    }

    try {
      new URL(formData.imageURL);
    } catch (e) {
      setImageError('Please enter a valid URL');
      setImagePreview(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      setImagePreview(formData.imageURL);
      setImageError('');
    };
    img.onerror = () => {
      setImageError('Could not load image from this URL');
      setImagePreview(null);
    };
    img.src = formData.imageURL;
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, category: value }));
    
    if (value === 'add_new') {
      setShowNewCategoryFields(true);
      setFormData(prev => ({ ...prev, category: '' }));
    } else {
      setShowNewCategoryFields(false);
      setNewCategory({ name: '', description: '' });
    }
  };

  const handleNewCategoryChange = (e) => {
    const { name, value } = e.target;
    setNewCategory(prev => ({ ...prev, [name]: value }));
  };

  const validateNewCategory = () => {
    if (!newCategory.name.trim()) {
      setError('Category Name is required');
      return false;
    }
    if (!newCategory.description.trim()) {
      setError('Category Description is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await verifySession();

      // If adding a new category
      let categoryId = formData.category;
      if (showNewCategoryFields) {
        if (!validateNewCategory()) {
          setLoading(false);
          return;
        }

        // First create the new category
        const categoryResponse = await axios.post('/categories/addNewCategory', {
          name: newCategory.name,
          description: newCategory.description
        }, {
          withCredentials: true
        });

        categoryId = categoryResponse.data.id;
      }

      // Parse metadata from the input text
      const metadata = parseMetadata(metadataInput);
      
      // Then create the product
      await axios.post('/products', {
        ...formData,
        metadata,
        category: categoryId
      }, {
        withCredentials: true
      });
      
      setSuccess('Product added successfully!');
      setTimeout(() => {
        setActiveView('products');
        navigate('/dashboard/products');
      }, 1500);
    } catch (error) {
      console.error('Error adding product:', error);
      if (error.response?.status === 401) {
        logout();
      } else {
        setError(error.response?.data?.message || 'Failed to add product');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="content-container">
      <h2 className="mb-4">Add New Product</h2>
      
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="mb-3">{success}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Product Name *</Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Image URL</Form.Label>
          <Form.Control
            type="text"
            name="imageURL"
            value={formData.imageURL}
            onChange={handleChange}
            onBlur={handleImagePreview}
            disabled={loading}
            placeholder="https://example.com/image.jpg"
          />
          {imageError && <div className="text-danger small mt-1">{imageError}</div>}
          {imagePreview && (
            <div className="mt-2">
              <div className="small text-muted mb-1">Image Preview:</div>
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="img-thumbnail" 
                style={{ maxWidth: '200px', maxHeight: '200px' }}
                onError={() => {
                  setImageError('Error loading image preview');
                  setImagePreview(null);
                }}
              />
            </div>
          )}
        </Form.Group>

       <Form.Group className="mb-3">
          <Form.Label>Unit of Measure (UOM)</Form.Label>
          {uomsLoading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Loading UOMs...</span>
            </div>
          ) : (
            <Form.Select
              name="uom"
              value={formData.uom}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Select a UOM (optional)</option>
              {uoms.map(uom => (
                <option key={uom.id} value={uom.label}>
                  {uom.label} {uom.description ? `- ${uom.description}` : ''}
                </option>
              ))}
            </Form.Select>
          )}
          <Form.Text className="text-muted">
            Select the unit of measure for this product
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Category *</Form.Label>
          <Form.Select
            name="category"
            value={formData.category || (showNewCategoryFields ? 'add_new' : '')}
            onChange={handleCategoryChange}
            required
            disabled={loading}
          >
            <option value="">Select a category</option>
            <option value="add_new">Add New Category</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Form.Select>

          {showNewCategoryFields && (
            <div className="mt-3">
              <Form.Group className="mb-3">
                <Form.Label>Category Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={newCategory.name}
                  onChange={handleNewCategoryChange}
                  maxLength={50}
                  disabled={loading}
                  required
                />
                <Form.Text className="text-muted">
                  {50 - newCategory.name.length} characters remaining
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Category Description *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={newCategory.description}
                  onChange={handleNewCategoryChange}
                  maxLength={500}
                  disabled={loading}
                  required
                />
                <Form.Text className="text-muted">
                  {500 - newCategory.description.length} characters remaining
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Metadata</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={metadataInput}
            onChange={(e) => setMetadataInput(e.target.value)}
            placeholder='Enter key:value pairs, one per line (e.g., "weight":"750g")'
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Enter metadata as key:value pairs, one per line. Example: weight:750g
          </Form.Text>
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button 
            variant="secondary" 
            onClick={() => {
              setActiveView('products');
              navigate('/dashboard/products');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner as="span" size="sm" animation="border" role="status" />
                <span className="ms-2">Saving...</span>
              </>
            ) : 'Save Product'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddNewProduct;